import React, { useCallback, useEffect, useState } from 'react';

import { bind } from 'bind-event-listener';
import type { MessageDescriptor } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags';
import type { TriggerProps } from '@atlaskit/popup';

import type { RemixHiddenOptionKind } from '../../types';

import {
	RemixOverlay as RemixOptionsOverlay,
	RemixPopup,
	type RemixOption,
	type RemixOptionKind,
	type RemixOptionSelectionParameters,
} from './remix-overlay';
import { RemixThinkingBar } from './remix-thinking-bar';

/**
 * Props for the Remix container composed entry point.
 */
interface RemixContainerProps {
	/** Called when the user dismisses the remix flow (e.g. closes the overlay or preview). */
	onClose: () => void;
	/** Optional callback when the user selects a remix type (e.g. chart, diagram, infographic, visualization, custom). */
	onOptionSelect?: (params: RemixOptionSelectionParameters) => void;
	/**
	 * Called when "remix flow" becomes active or inactive.
	 * Handles the selection highlight (e.g. purple highlight) and thinking state start/stop flow
	 */
	onRemixFlowActiveChange?: (active: boolean) => void;
	/** When true, the thinking bar is owned by the caller and should not render here. */
	hideThinkingBar?: boolean;
	/**
	 * When true, the thinking bar is hidden
	 * Use this when the ephemeral view (e.g. staging modal with the result) is already visible
	 * so the thinking state is not shown at the same time as the result.
	 */
	isEphemeralViewVisible?: boolean;
	/**
	 * Set of remix option types that should skip the thinking bar entirely
	 * and proceed directly to the staging modal.
	 */
	skipThinkingTypes?: Set<string>;
	/** Custom set of options to display in the overlay. Defaults to selection-level REMIX_OPTIONS. */
	options?: RemixOption[];
	/** Option kinds to hide from the chooser while keeping the public API strongly typed. */
	hiddenOptions?: readonly RemixHiddenOptionKind[];
	/** Custom subtitle message for the overlay header. */
	subtitle?: MessageDescriptor;
	/**
	 * Presentation mode for the remix chooser.
	 * - 'modal' (default): renders a centered ADS Modal
	 * - 'popup': renders an anchored ADS Popup next to a trigger element (e.g object level remix)
	 */
	presentation?: 'modal' | 'popup';
	/** Required when presentation is 'popup'. Render function for the trigger element. */
	trigger?: (triggerProps: TriggerProps) => React.ReactNode;
	/** Popup placement relative to the trigger. Only used when presentation is 'popup'. */
	popupPlacement?: 'left-end';
	popupOffset?: [number, number];
	/** Whether the popup is open. Only used when presentation is 'popup'. */
	isPopupOpen?: boolean;
	/** When this value changes the popup will reposition itself. */
	popupRepositionKey?: number;
	/** Replace the animated rainbow border with a loading progress bar. */
	showProgressBar?: boolean;
	/**
	 * When `showProgressBar`, rapidly fill to 100%. Flip this on once the underlying
	 * work completes, then gate the next UI state on `onCompleteAnimationFinished`.
	 */
	isComplete?: boolean;
	/**
	 * Optional controlled thinking state. When provided, this takes precedence over
	 * the container's local thinking state.
	 */
	isThinking?: boolean;
	/** Fires once the progress bar finishes animating to 100%. */
	onCompleteAnimationFinished?: () => void;
	/**
	 * The content type of the document being edited (e.g. `'jira.issue'`, `'confluence.page'`).
	 */
	contentType?: string;
	/**
	 * When set, the overlay option-picker is suppressed on the first render and thinking
	 * begins immediately. Used when a prompt is pre-supplied (e.g. from the AI Image
	 * Generation flow) so the user never sees the option-picker.
	 */
	autoSubmitUserPrompt?: string;
}

/**
 * Composed entry point for the Remix feature.
 *
 * Renders the RemixOptionsOverlay for the user to pick a remix type.
 * Thinking bar visibility is internal (flow active and not ephemeral visible).
 */
export function RemixContainer({
	onClose,
	onOptionSelect,
	onRemixFlowActiveChange,
	hideThinkingBar = false,
	isEphemeralViewVisible = false,
	skipThinkingTypes,
	options,
	hiddenOptions,
	subtitle,
	presentation = 'modal',
	trigger,
	popupPlacement,
	popupOffset,
	isPopupOpen = true,
	popupRepositionKey,
	showProgressBar = false,
	isComplete = false,
	isThinking: controlledIsThinking,
	onCompleteAnimationFinished,
	contentType,
	autoSubmitUserPrompt,
}: RemixContainerProps): React.ReactElement {
	// TODO: Remove local thinking state when cleaning up platform_editor_ai_remix_in_editor_loading.
	// When autoSubmitUserPrompt is set, initialise to true so the option-picker overlay is
	// never rendered on the first frame — the prompt is submitted immediately.
	const [localIsThinking, setLocalIsThinking] = useState(!!autoSubmitUserPrompt);
	const isThinking = controlledIsThinking ?? localIsThinking;
	const [selectedOptionType, setSelectedOptionType] = useState<RemixOptionKind | undefined>();

	const handleOptionSelect = useCallback(
		(params: RemixOptionSelectionParameters) => {
			if (!skipThinkingTypes?.has(params.type)) {
				setLocalIsThinking(true);
			}
			setSelectedOptionType(params.type);
			onRemixFlowActiveChange?.(true);
			onOptionSelect?.(params);
		},
		[onOptionSelect, onRemixFlowActiveChange, skipThinkingTypes],
	);

	const handleStopThinking = useCallback(() => {
		setLocalIsThinking(false);
		onRemixFlowActiveChange?.(false);
	}, [onRemixFlowActiveChange]);

	useEffect(() => {
		if (fg('platform_editor_ai_remix_in_editor_loading')) {
			return;
		}

		if (!isThinking) {
			return;
		}

		return bind(window, {
			type: 'keydown',
			listener: (e: KeyboardEvent) => {
				if (e.key === 'Escape') {
					handleStopThinking();
				}
			},
		});
	}, [isThinking, handleStopThinking]);

	// In popup mode, the trigger must always be rendered so the popup can anchor to it.
	// The popup content visibility is controlled by isPopupOpen.
	if (presentation === 'popup' && trigger) {
		const showThinkingBar = isThinking && !isEphemeralViewVisible && !hideThinkingBar;

		return (
			<>
				<RemixPopup
					isOpen={isPopupOpen && !isThinking}
					onClose={onClose}
					trigger={trigger}
					placement={popupPlacement}
					offset={popupOffset}
					onOptionSelect={handleOptionSelect}
					options={options}
					hiddenOptions={hiddenOptions}
					subtitle={subtitle}
					repositionKey={popupRepositionKey}
				/>
				{showThinkingBar && (
					<RemixThinkingBar
						onStop={handleStopThinking}
						optionType={selectedOptionType}
						showProgressBar={showProgressBar}
						isComplete={isComplete}
						onCompleteAnimationFinished={onCompleteAnimationFinished}
						contentType={contentType}
					/>
				)}
			</>
		);
	}

	// Modal mode (default)
	// While thinking: show thinking bar only when ephemeral is not yet visible; otherwise show nothing
	if (isThinking) {
		if (!isEphemeralViewVisible && !hideThinkingBar) {
			return (
				<RemixThinkingBar
					onStop={handleStopThinking}
					optionType={selectedOptionType}
					showProgressBar={showProgressBar}
					isComplete={isComplete}
					onCompleteAnimationFinished={onCompleteAnimationFinished}
					contentType={contentType}
				/>
			);
		}
		return <></>;
	}

	return (
		<RemixOptionsOverlay
			onClose={onClose}
			onOptionSelect={handleOptionSelect}
			options={options}
			hiddenOptions={hiddenOptions}
			subtitle={subtitle}
		/>
	);
}
